/**
 * 网络请求配置
 */
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import cookie from 'react-cookies';

import { EGlobalCookieKey } from '@/types/cookie';
import type { CommonResponse, PagingResponse } from '@/types/responseModel';
import getTimezone from '@/utils/timezone';

import { decrypt, encrypt, waitForAESKey } from './aes';
import { EVENT } from './event';
import { GetFingerPrint } from './fingerprint';
import { innerHost, isTrulyProd } from './host';
// import { generateToken } from './request-hash';
import { ROUTER_PATH } from './router';

const timezone = getTimezone();

const baseURL = process.env.NEXT_PUBLIC_PROXY_TARGET ? '/api/proxy' : innerHost();

const service = axios.create({
  baseURL,
  timeout: 150 * 1000, // 超时时间单位是ms
  headers: {
    'Content-Type': 'application/json', //'application/json;charset=UTF-8',
    platform: 'web',
    appId: 'os-internal',
    lang: cookie.load(EGlobalCookieKey.userLanguage) || 'en',
    ...(timezone ? { timeZone: timezone } : {})
  }
});

export function Request<T = unknown, D = any>(configParam: AxiosRequestConfig) {
  return service.request<T, AxiosResponse<T>, D>(configParam);
}

export function POST<T = unknown>(configParam: AxiosRequestConfig) {
  return service.request<T, AxiosResponse<T>>({
    ...configParam,
    method: 'post'
  });
}

export function GET<T = unknown>(configParam: AxiosRequestConfig) {
  return service.request<T, AxiosResponse<T>>({
    ...configParam,
    method: 'get'
  });
}

export function DELETE<T = unknown>(configParam: AxiosRequestConfig) {
  return service.request<T, AxiosResponse<T>>({
    ...configParam,
    method: 'delete'
  });
}

export const whiteList = [
  '/rest/os/identity/output/get/by/share',
  '/rest/os/main/speech/voice/clone',
  '/rest/os/homepage/exposure'
];

/**
 * http request 拦截器
 */
service.interceptors.request.use(
  async (config) => {
    const headers = config.headers ?? {};
    const timestamp = Date.now().toString();

    if (headers['Content-Type'] === 'application/json') {
      config.data = JSON.stringify(config.data);
    }

    const token = cookie.load(EGlobalCookieKey.token);
    const lang = cookie.load(EGlobalCookieKey.userLanguage);

    const fp = new GetFingerPrint();

    // Wait for fp to be retrieved, max wait 3 seconds
    let fpValue = fp.value;

    if (!fpValue) {
      const startTime = Date.now();

      while (!fpValue && Date.now() - startTime < 3000) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        fpValue = fp.value;
      }
    }

    if (token) {
      if (config.headers) {
        config.headers.token = token;
      } else {
        config.headers = { token };
      }
    }

    if (
      token &&
      isTrulyProd() &&
      !headers['Content-Type'].toString().includes('multipart/form-data') &&
      !whiteList.includes(config.url || '') &&
      !(window as any).inApp
    ) {
      // Only encrypt if AES key is ready (no waiting)
      const isKeyReady = await waitForAESKey(100);

      if (isKeyReady) {
        if (config.headers) {
          config.headers['encipher'] = true;
        } else {
          config.headers = { token, encipher: true };
        }

        config.data = encrypt(config.data);
      }
      // If key not ready, send unencrypted request without encipher header
    }

    config.headers = {
      // _csrf: generateToken(timestamp, config.method?.toUpperCase(), config.data, config.params),
      timestamp: timestamp,
      ...config.headers,
      ...(lang ? { lang } : {}),
      ...(fpValue ? { fp: fpValue } : {})
    };

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * http response 拦截器
 */
service.interceptors.response.use(
  (response) => {
    return decryptData(checkStatus(response));
  },
  (error) => {
    console.log('Error:', error);

    throw error;
  }
);

const decryptData = (response: any) => {
  try {
    // 目前 get 请求没有加密，当为字符串时（例如: /in/rest/general/user/task/get/name）去解密就一直在报错
    response.data.data = decrypt(response.data.data);

    return response;
  } catch (e) {
    console.error('AES Error.');

    return response;
  }
};

const checkStatus = (response: any) => {
  // server check
  if (response?.data?.code !== 0 && response?.data?.code != 200) {
    if (response?.data?.code === 401) {
      // token 失效
      cookie.remove(EGlobalCookieKey.token);
      cookie.remove(EGlobalCookieKey.wide_token, { domain: '.mindos.com' });
      cookie.remove(EGlobalCookieKey.wide_token, { domain: '.me.bot' });
      window.dispatchEvent(new Event(EVENT.TOKEN_EXPIRED));

      return response;
    }

    if (response?.data?.code === 410) {
      // 加密 失效
      cookie.remove(EGlobalCookieKey.encrypt_key);
      cookie.remove(EGlobalCookieKey.wide_token, { domain: '.mindos.com' });
      cookie.remove(EGlobalCookieKey.wide_token, { domain: '.me.bot' });
      window.dispatchEvent(new Event(EVENT.TOKEN_EXPIRED));

      return response;
    }

    if (response?.data?.code === 403) {
      // 账号已删除
      const origin = location.origin;
      const pathname = location.pathname;

      if (pathname !== ROUTER_PATH.over) {
        window.location.href = origin + ROUTER_PATH.over;
      }

      return;
    }

    if (response?.data?.code === 402) {
      // 找不到PA
      const origin = location.origin;
      const pathname = location.pathname;

      if (pathname !== ROUTER_PATH.personalAICreate) {
        window.location.href = origin + ROUTER_PATH.personalAICreate;
      }

      return;
    }

    if (
      response?.data?.code === 1 ||
      (response?.data?.code >= 500 && response?.data?.code <= 599)
    ) {
      return response;
    }

    return response;
  }

  // http check
  if (response.status >= 200 && response.status < 300) {
    // http 正常
    return response;
  }

  return response.json();
};

export type { CommonResponse, PagingResponse };

export default service;
